import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { expandFontTheme, mapCssValue, theme, useLocalizationContext, } from '@contrast-security-inc/design-system-foundations';
import { css, cx } from '@emotion/css';
import { listboxItemStyles, ListboxItem } from './ListboxItem';
import { fnOrVal } from '../shared';
import { findScrollContainer, setupSafelyCallItemToID } from '../SelectModules/utils';
import * as messages from './translations';
export var MESSAGE_TYPES;
(function (MESSAGE_TYPES) {
    MESSAGE_TYPES["LOADING"] = "LOADING";
    MESSAGE_TYPES["START_TYPING"] = "START_TYPING";
    MESSAGE_TYPES["ADDITIONAL_RESULTS"] = "ADDITIONAL_RESULTS";
    MESSAGE_TYPES["NONE"] = "NONE";
})(MESSAGE_TYPES || (MESSAGE_TYPES = {}));
const listboxContainerStyles = css `
  animation: show-listbox-container ease-out 0.12s forwards;
  ${expandFontTheme(theme.font.body)};
  background-color: ${theme.color.white};
  border-radius: 0 0 3px 3px;
  border: 1px solid ${theme.color.neutral60};
  border-top-width: 0px;
  box-shadow: 0 3px 4px -2px ${theme.color.neutral50};
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: none;

  @keyframes show-listbox-container {
    0% {
      clip-path: inset(0 0 100% 0);
    }

    100% {
      clip-path: inset(0);
    }
  }
`;
const listboxContainerInnerShadowStyles = css `
  box-shadow: 0 0px 10px 6px rgb(50, 50, 50, 0.08);
  height: 0;
  opacity: 1;
  position: sticky;
  top: 100%;
  transition: opacity 0.3s ease;
  width: 100%;
`;
const listboxStyles = (showListboxMessage) => css `
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  left: 0;
  margin: 0;
  padding: 0;
  width: 100%;

  li:first-child {
    margin-top: 12px;
  }

  li:last-child {
    margin-bottom: ${showListboxMessage ? '0;' : '16px;'};
  }
`;
const anchoredDividerStyles = (removeBottomMargin) => css `
  border-bottom: 1px solid ${theme.color.neutral50};
  margin: 12px 0;
  ${removeBottomMargin ? 'margin-bottom: 0;' : ''}
`;
const listboxMessageStyles = css `
  ${expandFontTheme(theme.font.metadata)};
  background-color: ${theme.color.white};
  box-sizing: border-box;
  color: ${theme.color.neutral60};
  display: flex;
  padding: 16px;
  width: 100%;
`;
const baseListboxPortalWrapperStyles = css `
  position: absolute;
  z-index: ${theme.zIndex.dropdown};
`;
export const ListboxMessage = ({ messageType, testId }) => {
    const { formatMessage } = useLocalizationContext();
    const determineMessageKeyToUse = () => {
        switch (messageType) {
            case MESSAGE_TYPES.LOADING:
                return 'loading';
            case MESSAGE_TYPES.START_TYPING:
                return 'startTypingToSeeResults';
            case MESSAGE_TYPES.ADDITIONAL_RESULTS:
                return 'additionalResultsAvailable';
            default:
                return 'startTypingToSeeResults';
        }
    };
    return (React.createElement("span", { className: listboxMessageStyles, "data-testid": `${testId}-listbox-message` }, formatMessage(messages, determineMessageKeyToUse())));
};
export const Listbox = ({ AllowCreateProps, allowItemTextWrap, customRender, filteredAnchoredItemsCount = 0, filteredItems = [], getItemProps, getMenuProps, highlightedIndex = 0, id, inputValue = '', isAsync = false, isDisabled, isLoading = false, isOpen, itemIsDisabled, itemToID, itemToString, listboxWidth = '100%', maxHeight = '250px', onListboxScroll, selectedItem, selectedItems, shouldShowAdditionalResultsMessage = false, testId, totalItemCount = 0, useCheckboxSelection, }) => {
    if (!itemToID && selectedItem && typeof selectedItem === 'object') {
        console.error('`itemToID` must be defined for use with an `item` object');
    }
    const visibleItemsCountPerPagination = 50;
    const [listboxCanScroll, setListboxCanScroll] = useState(false);
    const [listboxIsScrolledTop, setListboxIsScrolledTop] = useState(true);
    const [listboxIsScrolledBottom, setListboxIsScrolledBottom] = useState(false);
    const [visibleItemsEndIndex, setVisibleItemsEndIndex] = useState(visibleItemsCountPerPagination);
    const listboxRef = useRef(null);
    const listboxWrapperRef = useRef(null);
    const previousScrollTop = useRef(0);
    const filteredItemsLessSelectedCount = useMemo(() => {
        if (selectedItem) {
            return filteredItems.length - 1;
        }
        else {
            return filteredItems.length;
        }
    }, [filteredItems, selectedItem]);
    // Set a type for the listbox message component to know which i18n key to use
    const messageType = useMemo(() => {
        if (isLoading) {
            return MESSAGE_TYPES.LOADING;
        }
        else if (shouldShowAdditionalResultsMessage && totalItemCount > 0) {
            return MESSAGE_TYPES.ADDITIONAL_RESULTS;
        }
        else if (inputValue === '') {
            return MESSAGE_TYPES.START_TYPING;
        }
        else {
            return MESSAGE_TYPES.NONE;
        }
    }, [isLoading, shouldShowAdditionalResultsMessage, inputValue, totalItemCount]);
    const showListboxMessage = useMemo(() => {
        return isAsync && messageType !== MESSAGE_TYPES.NONE;
    }, [isAsync, messageType]);
    const showNoResultsItem = useMemo(() => {
        return filteredItemsLessSelectedCount === 0 && !isLoading && inputValue !== '';
    }, [filteredItemsLessSelectedCount, isLoading, inputValue]);
    const safelyCallItemToID = useCallback((listboxItem) => {
        return setupSafelyCallItemToID(itemToID, itemToString, listboxItem);
    }, [itemToID, itemToString]);
    // Visible items to render in the Listbox
    const visibleItems = useMemo(() => {
        return filteredItems.slice(0, visibleItemsEndIndex);
    }, [filteredItems, visibleItemsEndIndex]);
    const updateVisibleItemsEndIndex = useCallback((listboxWrapperScrollTop, listboxWrapperClientHeight, listboxWrapperScrollHeight) => {
        /**
         * Height is based on ~24px text height plus item padding.
         * Custom render items may be larger, but that's ok.
         */
        const approxHeightOfItem = 32;
        /**
         * Consider it near the bottom if the listbox is scrolled within 30 items of the bottom.
         * Capture scroll direction.
         */
        const scrolledNearBottom = listboxWrapperScrollTop + listboxWrapperClientHeight > listboxWrapperScrollHeight - approxHeightOfItem * 30;
        const scrollDirection = listboxWrapperScrollTop < previousScrollTop.current ? 'up' : 'down';
        /**
         * Add the next `visibleItemsCountPerPagination` number of items if:
         *   - Scroll direction is 'down'
         *   - The listbox is scrolled near the bottom
         */
        if (filteredItems.length && scrollDirection === 'down' && scrolledNearBottom) {
            setVisibleItemsEndIndex(visibleItems.length + visibleItemsCountPerPagination);
        }
    }, [filteredItems, setVisibleItemsEndIndex, visibleItems]);
    const handleListboxScroll = () => {
        if (listboxWrapperRef.current) {
            const listboxWrapperScrollTop = listboxWrapperRef.current.scrollTop || 0;
            const listboxWrapperClientHeight = listboxWrapperRef.current.clientHeight || 0;
            const listboxWrapperScrollHeight = listboxWrapperRef.current.scrollHeight || 0;
            setListboxIsScrolledTop(listboxWrapperScrollTop === 0);
            setListboxIsScrolledBottom(listboxWrapperScrollHeight - listboxWrapperClientHeight === listboxWrapperScrollTop);
            updateVisibleItemsEndIndex(listboxWrapperScrollTop, listboxWrapperClientHeight, listboxWrapperScrollHeight);
            previousScrollTop.current = listboxWrapperScrollTop;
        }
    };
    const listboxItemProps = {
        AllowCreateProps,
        allowItemTextWrap,
        getItemProps,
        inputValue,
        isDisabled,
        itemIsDisabled,
        itemToID,
        itemToString,
        testId,
        messages,
        customRender,
    };
    // Periodically check that the listbox is scrollable to render inner shadow els
    useEffect(() => {
        if (listboxWrapperRef.current) {
            const listboxWrapperHeight = listboxWrapperRef.current.getBoundingClientRect().height || 0;
            const listboxWrapperScrollHeight = listboxWrapperRef.current.scrollHeight || 0;
            setListboxCanScroll(listboxWrapperScrollHeight > listboxWrapperHeight);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredItems, inputValue, isOpen, listboxWrapperRef.current]);
    // Report the listbox scroll state to the parent
    useEffect(() => {
        if (onListboxScroll) {
            onListboxScroll({
                listboxCanScroll,
                listboxIsScrolledTop,
                listboxIsScrolledBottom,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listboxCanScroll, listboxIsScrolledTop, listboxIsScrolledBottom]);
    return (React.createElement("div", { ...getMenuProps() }, isOpen && !isDisabled && (React.createElement("div", { className: cx(listboxContainerStyles, css `
                max-height: ${maxHeight ? mapCssValue(maxHeight) : '250px'};
                width: ${mapCssValue(listboxWidth)};
              `), "data-testid": `${testId}-listbox-wrapper`, onScroll: handleListboxScroll, ref: listboxWrapperRef },
        listboxCanScroll && (React.createElement(React.Fragment, null,
            React.createElement("div", { className: cx(listboxContainerInnerShadowStyles, css `
                      top: 0;
                    `, listboxIsScrolledTop &&
                    css `
                        opacity: 0;
                      `), "data-testid": `${testId}-listbox-inner-shadow-top` }),
            React.createElement("div", { className: cx(listboxContainerInnerShadowStyles, listboxIsScrolledBottom &&
                    css `
                        opacity: 0;
                      `), "data-testid": `${testId}-listbox-inner-shadow-bottom` }))),
        React.createElement("ul", { className: listboxStyles(showListboxMessage), "data-testid": `${testId}-listbox`, id: id && `${id}-listbox`, ref: listboxRef },
            visibleItems.length > 0 && totalItemCount > 0
                ? visibleItems.map((item, index) => (React.createElement(React.Fragment, { key: fnOrVal(item, safelyCallItemToID, item) },
                    React.createElement(ListboxItem, { ...listboxItemProps, index: index, item: item, key: fnOrVal(item, safelyCallItemToID, item), isAnchoredItem: filteredAnchoredItemsCount > 0 && index < filteredAnchoredItemsCount, itemIsHighlighted: highlightedIndex === index, itemIsSelected: selectedItem ? safelyCallItemToID(selectedItem) === safelyCallItemToID(item) : false, selectedItems: selectedItems, useCheckboxSelection: useCheckboxSelection }),
                    filteredAnchoredItemsCount > 0 && index === filteredAnchoredItemsCount - 1 && (React.createElement("div", { className: anchoredDividerStyles(visibleItems.length === filteredAnchoredItemsCount && !showNoResultsItem && showListboxMessage), "data-testid": `${testId}-listbox-item-divider` })))))
                : visibleItems.length === 0 &&
                    totalItemCount > 0 && (React.createElement("li", { className: cx(listboxItemStyles, css `
                        cursor: default;
                      `) })),
            showNoResultsItem && React.createElement(ListboxItem, { ...listboxItemProps, index: 0, item: undefined, key: 0 })),
        showListboxMessage && React.createElement(ListboxMessage, { messageType: messageType, testId: testId })))));
};
export const ListboxPortal = (props) => {
    const [listboxPosition, setListboxPosition] = useState({ top: -1000, left: -1000, width: 0 });
    /**
     * Recalculate the position of the listbox if any of the following occur:
     *  - Viewport is resized
     *  - Parent is scrolled
     *  - Filtered items change
     */
    useEffect(() => {
        const scrollContainer = props.dropdownWrapperRef?.current
            ? findScrollContainer(props.dropdownWrapperRef?.current)
            : null;
        const calculatePosition = () => {
            const rect = props.dropdownWrapperRef?.current?.getBoundingClientRect();
            if (!rect)
                return;
            setListboxPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left,
                width: rect.width,
            });
        };
        calculatePosition(); // Calculate initial position
        !!scrollContainer && scrollContainer.addEventListener('scroll', calculatePosition);
        window.addEventListener('resize', calculatePosition);
        return () => {
            !!scrollContainer && scrollContainer.removeEventListener('scroll', calculatePosition);
            window.removeEventListener('resize', calculatePosition);
        };
    }, [props.dropdownWrapperRef, props.isOpen, props.filteredItems]);
    const listboxPortal = (React.createElement("div", { className: baseListboxPortalWrapperStyles, style: { top: listboxPosition.top, left: listboxPosition.left, width: listboxPosition.width }, "data-testid": `${props.testId}-listbox-portal` },
        React.createElement(Listbox, { ...props })));
    // Render the Listbox via a React portal
    return ReactDOM.createPortal(listboxPortal, document.body);
};
